import { StakingPool } from '@trustpad/launchpad/dist/staking/types';

import BN from 'bn.js';
import { toBN } from 'web3-utils';

import { DEC_NORM } from '~config/constants';
import { PairContract } from '~types';
import { call, normalizeDecimals, toEthN } from '~utils';

export const getStakingSymbol = (pool: StakingPool) => {
  const [mainToken, secondToken] = getStakingTokens(pool);
  return secondToken
    ? `${mainToken.symbol}/${secondToken.symbol}`
    : mainToken.symbol;
};

export const getStakingTokens = (pool: StakingPool) =>
  Array.isArray(pool.stakeToken)
    ? pool.stakeToken
    : [pool.stakeToken, undefined];

/**
 * To determine the price of an LP token we need: totalValueInUsd / totalSupply
 * - total supply from pairContract.totalSupply
 * - totalValueInUsd:
 *  - token (TPAD) price in USD * reserve 1
 *  - PLUS pair token (BNB) price in USD * reserve 2
 */
export async function getLpTokenPrice({
  pairContract,
  tokenPriceInUsd,
  getCurrencyPriceInUsd,
  tokenDecimals,
}: {
  pairContract: PairContract;
  tokenPriceInUsd: number;
  getCurrencyPriceInUsd: () => Promise<number>;
  tokenDecimals: number;
}) {
  if (!tokenPriceInUsd) {
    return 0;
  }

  const getPrice = (
    tokensBN: BN,
    currencyBN: BN,
    supplyBN: BN,
    currencyPriceInUsd: number,
  ) => {
    const tokensValue = tokensBN.muln(tokenPriceInUsd);
    const currencyValue = currencyBN.muln(currencyPriceInUsd);
    const totalValue = tokensValue.add(currencyValue);
    const price = totalValue.mul(DEC_NORM).div(supplyBN);

    // debug("useLPTokenPrice.getLPPrice", toEth(price), {
    //   address: pairContract.options.address,
    //   tokenPriceInUsd,
    //   currencyPriceInUsd,
    //   tokens: toEth(tokensBN),
    //   currency: toEth(currencyBN),
    //   supply: toEth(supplyBN),
    //   tokensValue: toEth(tokensValue),
    //   currencyValue: toEth(currencyValue),
    //   totalValue: toEth(totalValue),
    // });

    return toEthN(price);
  };

  const price = await Promise.all([
    call(pairContract, 'getReserves'),
    call(pairContract, 'totalSupply'),
    getCurrencyPriceInUsd(),
  ])
    .then(([reserves, totalSupply, currencyPriceInUsd]) => {
      const tokensBN = normalizeDecimals(
        toBN(reserves['_reserve0']),
        tokenDecimals,
      );
      const currencyBN = toBN(reserves['_reserve1']);
      const supplyBN = toBN(totalSupply);

      return getPrice(tokensBN, currencyBN, supplyBN, currencyPriceInUsd);
    })
    .catch((e) => {
      console.error('Failed to determine LP Token price', e);
      return 0;
    });

  return price;
}
